
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Database, Shield, Calendar, Activity, Camera, CreditCard, FileSignature, User, ArrowRight, CheckCircle, Clock, Trophy, Target } from 'lucide-react';
import Layout from '@/components/Layout';

const ERM = () => {
  const integrationAreas = [
    {
      area: 'Appointments',
      juvFunction: 'Show upcoming visits in app dashboard',
      patientNowFunction: 'Manage provider calendars',
      integrationType: '1-way sync (read)',
      icon: Calendar,
      color: 'text-blue-600'
    },
    {
      area: 'Treatment History',
      juvFunction: 'Display completed services & notes',
      patientNowFunction: 'Store full chart notes',
      integrationType: '2-way sync (read/write)',
      icon: Activity,
      color: 'text-green-600'
    },
    {
      area: 'Photos',
      juvFunction: 'Before/After preview in app',
      patientNowFunction: 'Store clinical photos',
      integrationType: '1-way push (write)',
      icon: Camera,
      color: 'text-purple-600'
    },
    {
      area: 'Payments & Invoices',
      juvFunction: 'Show credits used & balances',
      patientNowFunction: 'Process and record transactions',
      integrationType: '1-way sync (read)',
      icon: CreditCard,
      color: 'text-juv-terracotta'
    },
    {
      area: 'Consent Forms',
      juvFunction: 'Offer digital sign in app',
      patientNowFunction: 'Store forms and documentation',
      integrationType: '1-way push (write)',
      icon: FileSignature,
      color: 'text-indigo-600'
    },
    {
      area: 'Patient Profile',
      juvFunction: 'Enhance with wellness goals & credits',
      patientNowFunction: 'Manage demographic info',
      integrationType: 'SSO + sync',
      icon: User,
      color: 'text-emerald-600'
    }
  ];

  const roadmapPhases = {
    phase1: [
      'Sync patient records (name, phone, appointment date)',
      'Log treatments and upload photos',
      'Submit signed consents to PatientNow'
    ],
    phase2: [
      'Two-way sync for invoices and payments',
      'Real-time staff alerts',
      'Deep link access from JUV app to PatientNow chart',
      'Treatment plan sync across both systems'
    ]
  };

  const competitiveComparison = [
    {
      feature: 'Patient Wallet & Credits',
      repeatMD: '✅ Loyalty points',
      kleer: '❌ None',
      moxie: '❌ None',
      juv: '✅ Glow Credits with usage tracking'
    },
    {
      feature: 'Patient Dashboard',
      repeatMD: '✅ Branded App',
      kleer: '❌ Web only',
      moxie: '❌ None',
      juv: '✅ Mobile-first portal with photo log'
    },
    {
      feature: 'EMR Integration',
      repeatMD: '❌ None',
      kleer: '❌ None',
      moxie: '❌ None',
      juv: '✅ PatientNow 2-way sync (Phase 2)'
    },
    {
      feature: 'Before/After Gallery',
      repeatMD: '✅ Limited',
      kleer: '❌ None',
      moxie: '❌ None',
      juv: '✅ Uploads, AI match, and tracking'
    },
    {
      feature: 'Consent & Forms',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '✅',
      juv: '✅ Digitally submitted to EMR'
    },
    {
      feature: 'Staff Dashboard',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '✅ Checklists',
      juv: '✅ Redemptions + check-ins'
    },
    {
      feature: 'SOP / LMS Training',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '✅ Protocols',
      juv: '✅ Alma LMS + Launch Kits'
    },
    {
      feature: 'AI Assistant Layer',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '❌',
      juv: '✅ Miles, Devon, Alma, Giselle'
    },
    {
      feature: 'Membership Billing',
      repeatMD: '❌',
      kleer: '✅ Subscriptions',
      moxie: '❌',
      juv: '✅ $99/mo + Beauty Bank credit system'
    },
    {
      feature: 'Multi-location Scalability',
      repeatMD: '✅ Enterprise Ready',
      kleer: '✅ Available',
      moxie: '✅ Available',
      juv: '✅ Franchise-ready with HQ Portal'
    }
  ];

  return (
    <Layout showCTA={false}>
      <div className="min-h-screen bg-juv-cream-white">
        {/* Hero Section */}
        <section className="pt-24 pb-20 bg-gradient-to-br from-juv-cream-white to-juv-soft-blush relative overflow-hidden">
          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut rounded-full flex items-center justify-center shadow-glow animate-glow-pulse">
                  <Database className="w-8 h-8 text-white" />
                </div>
              </div>
              
              <h1 className="apple-headline text-juv-deep-chestnut mb-6">
                Smart ERM Integration
              </h1>
              
              <p className="apple-subheadline text-juv-deep-chestnut/70 mb-8 max-w-3xl mx-auto">
                We don't aim to reinvent the EMR. We're building a smart, member-first interface that works with PatientNow to deliver the modern medspa experience patients expect.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Integration Areas */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-8 text-center">How We Integrate with PatientNow</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {integrationAreas.map((area, index) => {
                  const IconComponent = area.icon;
                  return (
                    <motion.div
                      key={area.area}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card className="glass-card bg-white hover:shadow-glow transition-all duration-300 group h-full">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-juv-soft-blush rounded-lg flex items-center justify-center group-hover:animate-sparkle">
                              <IconComponent className={`w-5 h-5 ${area.color}`} />
                            </div>
                            <h4 className="font-semibold text-juv-deep-chestnut">{area.area}</h4>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="bg-juv-soft-blush/30 p-3 rounded-lg">
                              <p className="text-xs font-medium text-juv-terracotta mb-1">JUV Function</p>
                              <p className="text-sm text-juv-deep-chestnut">{area.juvFunction}</p>
                            </div>
                            
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs font-medium text-gray-600 mb-1">PatientNow Function</p>
                              <p className="text-sm text-juv-deep-chestnut">{area.patientNowFunction}</p>
                            </div>
                            
                            <div className="text-center">
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-juv-terracotta/10 text-juv-terracotta rounded-full text-xs font-medium">
                                <ArrowRight className="w-3 h-3" />
                                {area.integrationType}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="py-16 bg-juv-soft-blush/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-8 text-center">Integration Roadmap</h2>
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <h4 className="font-semibold text-green-700">Phase 1 - Live</h4>
                      </div>
                      <ul className="space-y-2">
                        {roadmapPhases.phase1.map((item, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-juv-deep-chestnut">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-blue-700">Phase 2 - Q3 Launch</h4>
                      </div>
                      <ul className="space-y-2">
                        {roadmapPhases.phase2.map((item, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-juv-deep-chestnut">
                            <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Competitive Comparison */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="bg-juv-cream-white py-12 px-6 md:px-20 text-center rounded-lg mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut rounded-full flex items-center justify-center shadow-glow animate-glow-pulse">
                    <Trophy className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-6">Why JUV Wins</h2>
                <p className="apple-body text-juv-deep-chestnut leading-relaxed max-w-3xl mx-auto">
                  We didn't build another loyalty app, billing portal, or checklist tool.
                  <br />
                  We built the entire ecosystem — and that's why JUV doesn't compete.
                  <br />
                  <span className="font-semibold text-juv-terracotta">It dominates.</span>
                </p>
              </motion.div>
            </div>

            <div className="max-w-7xl mx-auto">
              <Card className="glass-card bg-white border-juv-rose-clay/20 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="bg-juv-soft-blush hover:bg-juv-soft-blush">
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[200px]">Feature</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[150px] text-center">RepeatMD</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[150px] text-center">Kleer</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[150px] text-center">Moxie</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-terracotta min-w-[200px] text-center bg-juv-terracotta/10">
                            JUV 🧠
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {competitiveComparison.map((row, index) => (
                          <TableRow 
                            key={row.feature} 
                            className="hover:bg-juv-cream-white/50 transition-colors duration-200"
                          >
                            <TableCell className="py-4 px-6 font-medium text-juv-deep-chestnut">
                              {row.feature}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center text-juv-deep-chestnut">
                              {row.repeatMD}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center text-juv-deep-chestnut">
                              {row.kleer}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center text-juv-deep-chestnut">
                              {row.moxie}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center font-medium text-juv-terracotta bg-juv-terracotta/5">
                              {row.juv}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-12 text-center px-6 md:px-20"
            >
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8">
                  <Target className="w-12 h-12 text-juv-terracotta mx-auto mb-6 animate-glow-pulse" />
                  <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-6">The Bottom Line</h3>
                  <div className="space-y-4 text-juv-deep-chestnut">
                    <p className="apple-body">RepeatMD is a rewards app.</p>
                    <p className="apple-body">Kleer is a billing tool.</p>
                    <p className="apple-body">Moxie is a checklist system.</p>
                  </div>
                  <div className="mt-8 p-6 bg-juv-terracotta/10 rounded-lg">
                    <p className="text-xl font-semibold text-juv-terracotta">
                      💡 JUV is the operating system — for modern medspas.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* HIPAA Compliance */}
        <section className="py-16 bg-juv-deep-chestnut">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto">
              <Shield className="w-12 h-12 text-juv-soft-blush mx-auto mb-6 animate-glow-pulse" />
              <h2 className="text-3xl font-bold text-white mb-4">HIPAA-Compliant, Future-Proof</h2>
              <p className="text-white/80 mb-8">
                JUV is built to meet the highest standards of data security and patient privacy. All integrations with PatientNow are HIPAA-compliant and designed for scale — from one location to one hundred.
              </p>
              <Button size="lg" className="apple-button-primary bg-juv-terracotta hover:bg-juv-rose-clay text-white">
                Learn More About Security
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ERM;
